/**
 * Application store.
 *
 * The single place where a UI action becomes a DSP change. Every setter that
 * touches hardware or DSP settings ends in a recompile-and-push, which is what
 * guarantees the spec's central requirement: the UI cannot change a label
 * without changing the audio (spec 4).
 *
 * Deliberately NOT holding audio data in React state -- the AudioBuffer, the
 * compiled payloads and the meters live outside the render cycle and are pushed
 * through the engine's own event channel.
 */

import { create } from 'zustand';
import type {
  AmpProfile,
  AnyProfile,
  DacProfile,
  HardwareCategory,
  OutputProfile,
} from '../types/hardware';
import type { CableProfile } from '../types/hardware';
import { isCable, isHeadphone } from '../types/hardware';
import type {
  AbxTrial,
  CableSlotId,
  ComparisonMode,
  EngineSettings,
  LevelMatchSettings,
  ProjectFile,
  RoomSettings,
  SlotId,
  SourceAnalysis,
  SourceMetadata,
  StageBypass,
  SwitchEvent,
  SystemChain,
  SystemSlotKey,
} from '../types/system';
import { CABLE_SLOTS, CABLE_SLOT_LABEL, CABLE_SLOT_TYPE, DEFAULT_CABLE_LENGTH_M } from '../types/system';
import type { CompiledSystem } from '../types/compiled';
import type { StageStatus } from '../audio/graph';
import type { MeterData } from '../audio/engine';
import type { HeadroomResult } from '../dsp/compile';
import type { LevelMatchResult } from '../audio/levelMatch';

import { engine } from '../audio/engine';
import { compileSystem, computeHeadroom } from '../dsp/compile';
import { DEFAULT_SYSTEM, loadBuiltInDatabase, registry } from '../database';
import { makeDefaultChain, makeDefaultProject } from './defaults';
import { decodeFile, probeSampleRate, type ContainerInfo } from '../audio/decode';
import { analyseBuffer } from '../analysis/analyseSource';
import { matchSystems } from '../audio/levelMatch';
import * as storage from '../database/storage';

export interface LogEntry {
  id: number;
  at: number;
  level: 'info' | 'warn' | 'error';
  message: string;
}

export interface AppState {
  // ---- lifecycle -------------------------------------------------------
  engineReady: boolean;
  engineError: string | null;
  booting: boolean;

  // ---- project ---------------------------------------------------------
  project: ProjectFile;
  compiled: Record<SystemSlotKey, CompiledSystem | null>;
  headroom: Record<SystemSlotKey, HeadroomResult | null>;

  // ---- source ----------------------------------------------------------
  buffer: AudioBuffer | null;
  metadata: SourceMetadata | null;
  container: ContainerInfo | null;
  analysis: SourceAnalysis | null;
  analysing: boolean;
  analysisNote: string | null;
  loadingFile: boolean;

  // ---- transport -------------------------------------------------------
  playing: boolean;
  position: number;
  duration: number;
  looping: boolean;

  // ---- comparison ------------------------------------------------------
  activeSystem: SystemSlotKey;
  blind: boolean;
  abxTruth: SystemSlotKey | null;
  abxTrials: AbxTrial[];
  levelMatchResult: LevelMatchResult | null;
  levelMatching: boolean;

  // ---- banks (spec 90) -------------------------------------------------
  activeBank: Record<SlotId, number>;

  // ---- telemetry -------------------------------------------------------
  status: Record<SystemSlotKey, Record<string, StageStatus>>;
  meters: MeterData | null;
  limiterEngaged: boolean;
  limiterMaxReduction: number;
  logs: LogEntry[];
  history: SwitchEvent[];

  // ---- undo ------------------------------------------------------------
  undoStack: ProjectFile[];
  redoStack: ProjectFile[];

  // ---- ui --------------------------------------------------------------
  favourites: Set<string>;
  openPanel: string | null;
  librarySlot: SlotId | CableSlotId | null;
  librarySystem: SystemSlotKey;
  inspectorNode: string | null;
}

export interface AppActions {
  boot(): Promise<void>;
  loadFile(file: File): Promise<void>;
  play(): void;
  pause(): void;
  togglePlay(): void;
  stop(): void;
  seek(seconds: number): void;
  nudge(seconds: number): void;
  setLoop(on: boolean): void;

  setProfile(system: SystemSlotKey, slot: SlotId, profileId: string | null): void;
  setBank(slot: SlotId, index: number, profileId: string | null): void;
  selectBank(slot: SlotId, index: number): void;
  setChain(system: SystemSlotKey, patch: Partial<SystemChain>): void;
  setRoom(system: SystemSlotKey, patch: Partial<RoomSettings>): void;
  setBypass(system: SystemSlotKey, key: keyof StageBypass, value: boolean): void;
  setCable(system: SystemSlotKey, slot: CableSlotId, profileId: string | null): void;
  setCableLength(system: SystemSlotKey, slot: CableSlotId, lengthM: number): void;
  cableFor(system: SystemSlotKey, slot: CableSlotId): CableProfile | null;
  setEngine(patch: Partial<EngineSettings>): void;
  setLevelMatch(patch: Partial<LevelMatchSettings>): void;
  setVolume(system: SystemSlotKey, v: number): void;
  setMasterGain(db: number): void;
  toggleMute(): void;

  setComparison(mode: ComparisonMode): void;
  switchSystem(key: SystemSlotKey): void;
  toggleAb(): void;
  startAbx(): void;
  answerAbx(choice: SystemSlotKey, confidence: 1 | 2 | 3 | 4 | 5, notes?: string): void;
  setBlind(on: boolean): void;
  runLevelMatch(): Promise<void>;
  copySystem(from: SystemSlotKey, to: SystemSlotKey): void;

  undo(): void;
  redo(): void;
  pushUndo(): void;

  toggleFavourite(id: string): void;
  openLibrary(slot: SlotId | CableSlotId, system: SystemSlotKey): void;
  closeLibrary(): void;
  setPanel(p: string | null): void;
  setInspector(node: string | null): void;
  log(level: 'info' | 'warn' | 'error', message: string): void;

  recompile(system?: SystemSlotKey, transitionMs?: number): void;
  profileFor(system: SystemSlotKey, slot: SlotId): AnyProfile | null;
  saveProjectAs(name: string): Promise<void>;
  loadProjectByName(name: string): Promise<void>;
  exportProject(): string;
  importProject(json: string): void;
  resetAll(): void;
}

let logId = 1;
let recompileTimer: ReturnType<typeof setTimeout> | null = null;

const SLOT_TO_CATEGORY: Record<SlotId, HardwareCategory[]> = {
  dac: ['dac'],
  amp: ['amp'],
  output: ['speaker', 'headphone'],
};

function chainOf(p: ProjectFile, key: SystemSlotKey): SystemChain {
  return key === 'A' ? p.systemA : p.systemB;
}

function withChain(p: ProjectFile, key: SystemSlotKey, next: SystemChain): ProjectFile {
  return key === 'A'
    ? { ...p, systemA: next, modified: new Date().toISOString() }
    : { ...p, systemB: next, modified: new Date().toISOString() };
}

/** Strip everything that must not participate in undo (spec 75). */
function undoSnapshot(p: ProjectFile): ProjectFile {
  return JSON.parse(JSON.stringify({ ...p, source: undefined })) as ProjectFile;
}

export const useApp = create<AppState & AppActions>((set, get) => ({
  engineReady: false,
  engineError: null,
  booting: false,

  project: makeDefaultProject(),
  compiled: { A: null, B: null },
  headroom: { A: null, B: null },

  buffer: null,
  metadata: null,
  container: null,
  analysis: null,
  analysing: false,
  analysisNote: null,
  loadingFile: false,

  playing: false,
  position: 0,
  duration: 0,
  looping: false,

  activeSystem: 'A',
  blind: false,
  abxTruth: null,
  abxTrials: [],
  levelMatchResult: null,
  levelMatching: false,

  activeBank: { dac: 0, amp: 0, output: 0 },

  status: { A: {}, B: {} },
  meters: null,
  limiterEngaged: false,
  limiterMaxReduction: 0,
  logs: [],
  history: [],

  undoStack: [],
  redoStack: [],

  favourites: new Set(),
  openPanel: null,
  librarySlot: null,
  librarySystem: 'A',
  inspectorNode: null,

  // -------------------------------------------------------------------------

  log(level, message) {
    set((s) => ({
      logs: [...s.logs, { id: logId++, at: Date.now(), level, message }].slice(-500),
    }));
  },

  async boot() {
    if (get().booting || get().engineReady) return;
    set({ booting: true, engineError: null });
    loadBuiltInDatabase();

    // User-imported profiles join the same registry as the built-ins.
    try {
      const custom = await storage.listCustomProfiles();
      if (custom.length) {
        registry.register(custom);
        get().log('info', `Loaded ${custom.length} custom profile${custom.length === 1 ? '' : 's'} from local storage.`);
      }
      const favs = await storage.loadPref<string[]>('favourites');
      if (favs) set({ favourites: new Set(favs) });
      const hist = await storage.loadHistory();
      if (hist) set({ history: hist });
      const savedDefault = await storage.loadPref<ProjectFile>('defaultProject');
      if (savedDefault) set({ project: savedDefault });
    } catch {
      /* storage is best-effort; the app runs without it */
    }

    engine.on((e) => {
      switch (e.type) {
        case 'state':
          set({ playing: e.playing });
          break;
        case 'position':
          set({ position: e.seconds, duration: e.duration });
          break;
        case 'status':
          set((s) => ({
            status: { ...s.status, [e.system]: { ...s.status[e.system], [e.status.stage]: e.status } },
          }));
          break;
        case 'meters':
          set({ meters: e.data });
          break;
        case 'limiter':
          set({ limiterEngaged: e.engaged, limiterMaxReduction: e.maxReductionDb });
          break;
        case 'error':
          get().log('error', e.message);
          if (e.fatal) set({ engineError: e.message });
          break;
        case 'log':
          get().log(e.level, e.message);
          break;
        case 'ended':
          break;
      }
    });

    engine.onOverload = ({ ratio }) => {
      const st = get();
      const eng = st.project.engine;
      const order: Array<EngineSettings['performance']> = ['maximum-quality', 'balanced', 'low-latency'];
      const realismOrder: Array<EngineSettings['realism']> = ['maximum', 'high', 'standard'];
      // Step realism down first -- it costs the most and matters least at the
      // point where the machine is already struggling.
      const ri = realismOrder.indexOf(eng.realism);
      if (ri < realismOrder.length - 1) {
        const next = realismOrder[ri + 1];
        get().log('warn', `The audio thread is running at ${(ratio * 100).toFixed(0)}% of real time and is dropping audio. Realism reduced from ${eng.realism} to ${next} automatically. Raise it again in the engine panel if this was a one-off.`);
        get().setEngine({ realism: next });
        return;
      }
      const pi = order.indexOf(eng.performance);
      if (pi < order.length - 1) {
        const next = order[pi + 1];
        get().log('warn', `The audio thread is still behind at ${(ratio * 100).toFixed(0)}% of real time. Performance mode reduced from ${eng.performance} to ${next}.`);
        get().setEngine({ performance: next });
        return;
      }
      get().log('error', `The audio thread is running at ${(ratio * 100).toFixed(0)}% of real time even at the lightest settings. This machine cannot sustain the current chain; try a simpler DAC filter or turn off the room model.`);
    };

    try {
      await engine.init();
      set({ engineReady: true, booting: false });
      get().recompile(undefined, 0);
      const eng = get().project.engine;
      engine.setMasterGainDb(eng.muted ? -200 : eng.masterGainDb, 0);
      engine.setLimiter(eng.safetyLimiter);
      engine.setAbRampMs(eng.transitionMs);
      engine.setSingleSystem(get().project.comparison === 'single');
    } catch (err) {
      set({ booting: false, engineError: (err as Error).message });
    }
  },

  async loadFile(file) {
    set({ loadingFile: true, analysisNote: null });
    try {
      const { rate, info } = await probeSampleRate(file);
      const eng = get().project.engine;

      // Recreate the context only when the user has explicitly asked to follow
      // the source rate. This is the ONE operation that rebuilds the graph, it
      // is slow, and on some platforms creating a context at a non-device rate
      // blocks -- so it is opt-in, guarded, and never fatal.
      if (eng.followSourceRate && rate && engine.sampleRate !== rate) {
        get().log('info', `Source is ${rate} Hz; rebuilding the audio context to match so the chain runs at the file's native rate.`);
        try {
          await engine.init(rate);
          get().recompile(undefined, 0);
          engine.setMasterGainDb(eng.muted ? -200 : eng.masterGainDb, 0);
          engine.setLimiter(eng.safetyLimiter);
        } catch (err) {
          get().log(
            'warn',
            `Could not open an audio context at ${rate} Hz (${(err as Error).message}). Continuing at ${engine.sampleRate} Hz; the browser will resample the source, which is reported in the engine panel.`,
          );
          if (!engine.ready) await engine.init();
        }
      } else if (rate && engine.sampleRate !== rate) {
        get().log(
          'info',
          `Source is ${rate} Hz and the audio context runs at ${engine.sampleRate} Hz, so the browser resamples on decode. Enable "follow source rate" in the engine panel to run the chain at the file's native rate instead.`,
        );
      }

      if (!engine.ctx) throw new Error('Audio engine is not running.');
      const decoded = await decodeFile(file, engine.ctx);
      engine.setBuffer(decoded.buffer);
      set({
        buffer: decoded.buffer,
        metadata: decoded.metadata,
        container: decoded.container,
        analysis: null,
        duration: decoded.buffer.duration,
        position: 0,
        loadingFile: false,
      });
      for (const w of info.warnings) get().log('warn', w);
      for (const w of decoded.container.warnings) get().log('warn', w);
      get().log(
        'info',
        `Loaded ${decoded.metadata.fileName}: ${decoded.metadata.container}, ${decoded.metadata.codec}, ${decoded.metadata.sampleRate} Hz, ${decoded.metadata.channels} ch, ${decoded.buffer.duration.toFixed(2)} s.`,
      );
      get().recompile(undefined, 0);

      set({ analysing: true });
      analyseBuffer(decoded.buffer, (stage) => set({ analysisNote: stage }))
        .then(({ analysis, elapsedMs, onMainThread }) => {
          set({ analysis, analysing: false, analysisNote: null });
          get().log(
            'info',
            `Analysed in ${elapsedMs.toFixed(0)} ms${onMainThread ? ' (on the main thread -- worker unavailable)' : ''}: ${analysis.lufsIntegrated.toFixed(1)} LUFS, peak ${analysis.peakDbFS.toFixed(2)} dBFS, crest ${analysis.crestFactorDb.toFixed(1)} dB.`,
          );
        })
        .catch((err) => {
          set({ analysing: false, analysisNote: null });
          get().log('error', `Source analysis failed: ${err.message}`);
        });
    } catch (err) {
      set({ loadingFile: false });
      const e = err as Error & { detail?: string };
      get().log('error', `${e.message}${e.detail ? ` ${e.detail}` : ''}`);
      set({ engineError: null });
      throw err;
    }
  },

  play() {
    void engine.resume().then(() => engine.play());
  },
  pause() {
    engine.pause();
  },
  togglePlay() {
    if (get().playing) engine.pause();
    else get().play();
  },
  stop() {
    engine.stop();
  },
  seek(seconds) {
    engine.seek(seconds);
    set({ position: seconds });
  },
  nudge(seconds) {
    get().seek(Math.max(0, Math.min(get().duration, get().position + seconds)));
  },
  setLoop(on) {
    engine.setLoop(on);
    set({ looping: on });
  },

  // -------------------------------------------------------------------------

  profileFor(system, slot) {
    const c = chainOf(get().project, system);
    const id = slot === 'dac' ? c.dacId : slot === 'amp' ? c.ampId : c.outputId;
    return registry.get(id);
  },

  setProfile(system, slot, profileId) {
    const state = get();
    const chain = chainOf(state.project, system);
    const prevId = slot === 'dac' ? chain.dacId : slot === 'amp' ? chain.ampId : chain.outputId;
    if (prevId === profileId) return;

    const next: SystemChain = {
      ...chain,
      ...(slot === 'dac' ? { dacId: profileId } : slot === 'amp' ? { ampId: profileId } : { outputId: profileId }),
    };
    // Selecting a new DAC resets any filter choice that no longer exists.
    if (slot === 'dac') {
      const p = registry.get(profileId) as DacProfile | null;
      const has = p?.filters.some((f) => f.id === chain.dac.filterId);
      if (!has) next.dac = { ...next.dac, filterId: p?.defaultFilterId ?? p?.filters[0]?.id };
    }

    state.pushUndo();
    const event: SwitchEvent = {
      timestamp: Date.now(),
      slot,
      system,
      fromId: prevId,
      toId: profileId,
      fromLabel: labelOf(prevId),
      toLabel: labelOf(profileId),
      transitionMs: state.project.engine.transitionMs,
    };
    set((s) => ({
      project: withChain(s.project, system, next),
      history: [...s.history, event].slice(-2000),
    }));
    void storage.appendHistory([event]);
    get().recompile(system);
    get().log('info', `${system} ${slot.toUpperCase()}: ${event.fromLabel} -> ${event.toLabel} (${event.transitionMs} ms transition)`);
  },

  setBank(slot, index, profileId) {
    set((s) => {
      const banks = { ...s.project.banks, [slot]: s.project.banks[slot].map((v, i) => (i === index ? profileId : v)) };
      return { project: { ...s.project, banks } };
    });
  },

  selectBank(slot, index) {
    const id = get().project.banks[slot]?.[index];
    if (!id) return;
    set((s) => ({ activeBank: { ...s.activeBank, [slot]: index } }));
    get().setProfile(get().activeSystem, slot, id);
  },

  setChain(system, patch) {
    set((s) => ({ project: withChain(s.project, system, { ...chainOf(s.project, system), ...patch }) }));
    scheduleRecompile(get, system);
  },

  setRoom(system, patch) {
    set((s) => {
      const c = chainOf(s.project, system);
      return { project: withChain(s.project, system, { ...c, room: { ...c.room, ...patch } }) };
    });
    scheduleRecompile(get, system);
  },

  setBypass(system, key, value) {
    get().pushUndo();
    set((s) => {
      const c = chainOf(s.project, system);
      return { project: withChain(s.project, system, { ...c, bypass: { ...c.bypass, [key]: value } }) };
    });
    // Stage bypass is a direct message, not a recompile: it must be instant.
    const map: Partial<Record<keyof StageBypass, 'dac' | 'amp' | 'output' | 'spatial'>> = {
      dacConversion: 'dac', dacDigitalFilter: 'dac', dacAnalog: 'dac',
      ampNonlinear: 'amp', ampInput: 'amp', ampOutput: 'amp',
      speaker: 'output', room: 'spatial', spatial: 'spatial',
    };
    const stage = map[key];
    if (stage) engine.setBypass(system, stage, value);
    get().log('info', `${system} ${String(key)}: ${value ? 'bypassed' : 'active'}`);
  },

  cableFor(system, slot) {
    const c = chainOf(get().project, system).cables?.[slot];
    const p = registry.get(c?.profileId ?? null);
    return p && isCable(p) ? p : null;
  },

  setCable(system, slot, profileId) {
    const state = get();
    const chain = chainOf(state.project, system);
    const prev = chain.cables[slot];
    if (prev.profileId === profileId) return;

    // Adopt the new cable's own default length, unless the user has already
    // moved this slot away from the slot default -- in which case their length
    // is the more informed choice and is kept.
    const next = registry.get(profileId);
    const slotDefault = DEFAULT_CABLE_LENGTH_M[slot];
    const userMovedLength = Math.abs(prev.lengthM - slotDefault) > 1e-6;
    const lengthM =
      !userMovedLength && next && isCable(next) ? next.defaultLengthM : prev.lengthM;

    state.pushUndo();
    const event: SwitchEvent = {
      timestamp: Date.now(),
      slot: 'cable',
      system,
      fromId: prev.profileId,
      toId: profileId,
      fromLabel: labelOf(prev.profileId),
      toLabel: labelOf(profileId),
      transitionMs: state.project.engine.transitionMs,
    };
    set((st) => ({
      project: withChain(st.project, system, {
        ...chain,
        cables: { ...chain.cables, [slot]: { profileId, lengthM } },
      }),
      history: [...st.history, event].slice(-2000),
    }));
    void storage.appendHistory([event]);
    get().recompile(system);

    const modelled = next && isCable(next) ? next.cableType === 'speaker' || next.cableType === 'interconnect' : false;
    get().log(
      'info',
      `${system} ${CABLE_SLOT_LABEL[slot]}: ${event.fromLabel} -> ${event.toLabel}` +
        (profileId && !modelled
          ? '. No signal processing is applied for this cable type; the output is bit-identical.'
          : ''),
    );
  },

  setCableLength(system, slot, lengthM) {
    const chain = chainOf(get().project, system);
    const clamped = Math.max(0.1, Math.min(30, lengthM));
    set((st) => ({
      project: withChain(st.project, system, {
        ...chain,
        cables: { ...chain.cables, [slot]: { ...chain.cables[slot], lengthM: clamped } },
      }),
    }));
    // Length is a continuous control, so it debounces like the room sliders.
    scheduleRecompile(get, system);
  },

  setEngine(patch) {
    const prev = get().project.engine;
    set((s) => ({ project: { ...s.project, engine: { ...s.project.engine, ...patch } } }));
    const eng = get().project.engine;

    if (patch.masterGainDb !== undefined || patch.muted !== undefined) {
      engine.setMasterGainDb(eng.muted ? -200 : eng.masterGainDb);
    }
    if (patch.safetyLimiter !== undefined) engine.setLimiter(eng.safetyLimiter);
    if (patch.transitionMs !== undefined) engine.setAbRampMs(eng.transitionMs);

    const needsRecompile =
      patch.realism !== undefined ||
      patch.performance !== undefined ||
      patch.oversample !== undefined ||
      patch.experimental !== undefined;
    if (needsRecompile) {
      get().log(
        'info',
        `Quality: realism ${eng.realism}, performance ${eng.performance}${eng.oversample !== 'auto' ? `, oversampling forced to ${eng.oversample}x` : ''}. Recompiling both chains.`,
      );
      scheduleRecompile(get);
    }
    if (patch.followSourceRate !== undefined && patch.followSourceRate !== prev.followSourceRate) {
      get().log('info', 'Sample-rate following changed. It takes effect the next time a file is loaded, because changing the context rate requires a new AudioContext.');
    }
  },

  setLevelMatch(patch) {
    set((s) => ({ project: { ...s.project, levelMatch: { ...s.project.levelMatch, ...patch } } }));
    const lm = get().project.levelMatch;
    if (lm.mode === 'off') {
      engine.setSystemTrim('A', 0);
      engine.setSystemTrim('B', 0);
      set({ levelMatchResult: null });
    } else {
      void get().runLevelMatch();
    }
  },

  setVolume(system, v) {
    set((s) => {
      const c = chainOf(s.project, system);
      return { project: withChain(s.project, system, { ...c, amp: { ...c.amp, volume: v } }) };
    });
    engine.setStageParam(system, 'amp', 'volume', v);
  },

  setMasterGain(db) {
    get().setEngine({ masterGainDb: db });
  },

  toggleMute() {
    get().setEngine({ muted: !get().project.engine.muted });
  },

  // -------------------------------------------------------------------------

  setComparison(mode) {
    set((s) => ({ project: { ...s.project, comparison: mode } }));
    engine.setSingleSystem(mode === 'single');
    if (mode === 'single') {
      set({ blind: false, abxTruth: null });
      get().switchSystem('A');
    }
  },

  switchSystem(key) {
    engine.setActiveSystem(key, get().project.engine.transitionMs);
    set({ activeSystem: key });
  },

  toggleAb() {
    const next = get().activeSystem === 'A' ? 'B' : 'A';
    get().switchSystem(next);
    if (!get().blind) get().log('info', `A/B -> System ${next}`);
  },

  startAbx() {
    const truth: SystemSlotKey = Math.random() < 0.5 ? 'A' : 'B';
    set({ abxTruth: truth, blind: true, project: { ...get().project, comparison: 'abx' } });
    engine.setActiveSystem(truth, get().project.engine.transitionMs);
    set({ activeSystem: truth });
    get().log('info', `ABX trial ${get().abxTrials.length + 1} started. X has been randomised and identities are hidden.`);
  },

  answerAbx(choice, confidence, notes) {
    const truth = get().abxTruth;
    if (!truth) return;
    const trial: AbxTrial = {
      index: get().abxTrials.length + 1,
      truth,
      choice,
      correct: choice === truth,
      confidence,
      notes,
      timestamp: Date.now(),
    };
    set((s) => ({ abxTrials: [...s.abxTrials, trial], abxTruth: null }));
    const trials = get().abxTrials;
    const correct = trials.filter((t) => t.correct).length;
    get().log(
      'info',
      `ABX trial ${trial.index}: chose ${choice}, X was ${truth} -- ${trial.correct ? 'correct' : 'incorrect'}. Running score ${correct}/${trials.length}.`,
    );
  },

  setBlind(on) {
    set({ blind: on });
  },

  async runLevelMatch() {
    const s = get();
    const lm = s.project.levelMatch;
    if (lm.mode === 'off' || !s.buffer) {
      engine.setSystemTrim('A', lm.manualTrimA);
      engine.setSystemTrim('B', lm.manualTrimB);
      return;
    }
    set({ levelMatching: true });
    try {
      const build = (key: SystemSlotKey) => ({
        buffer: s.buffer!,
        chain: chainOf(s.project, key),
        dac: s.profileFor(key, 'dac') as DacProfile | null,
        amp: s.profileFor(key, 'amp') as AmpProfile | null,
        output: s.profileFor(key, 'output') as OutputProfile | null,
        engine: s.project.engine,
        sourceRate: s.metadata?.sampleRate ?? 44100,
        resolveCable,
      });
      const result = await matchSystems({
        buffer: s.buffer,
        chains: { A: build('A'), B: build('B') },
        mode: lm.mode,
        manualTrimA: lm.manualTrimA,
        manualTrimB: lm.manualTrimB,
      });
      engine.setSystemTrim('A', result.trimA);
      engine.setSystemTrim('B', result.trimB);
      set({ levelMatchResult: result, levelMatching: false });
      get().log('info', result.summary);
    } catch (err) {
      set({ levelMatching: false });
      get().log('error', `Level matching failed: ${(err as Error).message}`);
    }
  },

  copySystem(from, to) {
    get().pushUndo();
    const src = chainOf(get().project, from);
    const dst = chainOf(get().project, to);
    set((s) => ({ project: withChain(s.project, to, { ...src, id: dst.id, name: dst.name }) }));
    get().recompile(to);
    get().log('info', `Copied System ${from} to System ${to}.`);
  },

  // -------------------------------------------------------------------------

  pushUndo() {
    set((s) => ({
      undoStack: [...s.undoStack, undoSnapshot(s.project)].slice(-100),
      redoStack: [],
    }));
  },

  undo() {
    const s = get();
    const prev = s.undoStack[s.undoStack.length - 1];
    if (!prev) return;
    set({
      project: { ...prev, source: s.project.source },
      undoStack: s.undoStack.slice(0, -1),
      redoStack: [...s.redoStack, undoSnapshot(s.project)].slice(-100),
    });
    get().recompile(undefined, get().project.engine.transitionMs);
    get().log('info', 'Undo.');
  },

  redo() {
    const s = get();
    const next = s.redoStack[s.redoStack.length - 1];
    if (!next) return;
    set({
      project: { ...next, source: s.project.source },
      redoStack: s.redoStack.slice(0, -1),
      undoStack: [...s.undoStack, undoSnapshot(s.project)].slice(-100),
    });
    get().recompile(undefined, get().project.engine.transitionMs);
    get().log('info', 'Redo.');
  },

  // -------------------------------------------------------------------------

  toggleFavourite(id) {
    set((s) => {
      const next = new Set(s.favourites);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      void storage.savePref('favourites', [...next]);
      return { favourites: next };
    });
  },

  openLibrary(slot, system) {
    set({ librarySlot: slot, librarySystem: system, openPanel: 'library' });
  },
  closeLibrary() {
    set({ librarySlot: null, openPanel: null });
  },
  setPanel(p) {
    set({ openPanel: p });
  },
  setInspector(node) {
    set({ inspectorNode: node });
  },

  // -------------------------------------------------------------------------

  recompile(system, transitionMs) {
    const s = get();
    if (!engine.ready) return;
    const fs = engine.sampleRate;
    const sourceRate = s.metadata?.sampleRate ?? fs;
    const ms = transitionMs ?? s.project.engine.transitionMs;
    const keys: SystemSlotKey[] = system ? [system] : ['A', 'B'];

    const nextCompiled = { ...s.compiled };
    const nextHeadroom = { ...s.headroom };

    for (const key of keys) {
      const chain = chainOf(s.project, key);
      const dac = registry.get(chain.dacId) as DacProfile | null;
      const amp = registry.get(chain.ampId) as AmpProfile | null;
      const output = registry.get(chain.outputId) as OutputProfile | null;
      try {
        const sys = compileSystem({
          chain, dac, amp, output,
          engine: s.project.engine,
          fs,
          sourceRate,
          resolveCable,
        });
        engine.applySystem(key, sys, ms);
        engine.setStageParam(key, 'amp', 'volume', chain.amp.volume);
        engine.setStageParam(key, 'output', 'balanceDb', chain.headphone.balanceDb);
        engine.setStageParam(key, 'dac', 'trimDb', chain.dac.trimDb);
        nextCompiled[key] = sys;
        nextHeadroom[key] = computeHeadroom(amp, output);
        for (const w of sys.warnings) get().log('warn', `System ${key}: ${w}`);
      } catch (err) {
        get().log('error', `System ${key} failed to compile: ${(err as Error).message}`);
      }
    }
    set({ compiled: nextCompiled, headroom: nextHeadroom });
  },

  async saveProjectAs(name) {
    const p = { ...get().project, name, modified: new Date().toISOString() };
    set({ project: p });
    await storage.saveProject(p);
    get().log('info', `Saved project "${name}".`);
  },

  async loadProjectByName(name) {
    const p = await storage.loadProject(name);
    if (!p) {
      get().log('error', `No saved project named "${name}".`);
      return;
    }
    get().pushUndo();
    set({ project: p });
    get().recompile(undefined, 0);
    get().log('info', `Loaded project "${name}".`);
  },

  exportProject() {
    return JSON.stringify({ ...get().project, source: undefined }, null, 2);
  },

  importProject(json) {
    try {
      const parsed = JSON.parse(json) as ProjectFile;
      if (parsed.formatVersion !== 1) throw new Error(`Unsupported project format version ${parsed.formatVersion}.`);
      if (!parsed.systemA || !parsed.systemB) throw new Error('Project is missing a system definition.');
      // Validate referenced hardware still exists rather than silently
      // producing a chain with missing components.
      const missing: string[] = [];
      for (const key of ['A', 'B'] as SystemSlotKey[]) {
        const c = chainOf(parsed, key);
        for (const id of [c.dacId, c.ampId, c.outputId]) {
          if (id && !registry.has(id)) missing.push(id);
        }
      }
      get().pushUndo();
      set({ project: { ...makeDefaultProject(), ...parsed } });
      get().recompile(undefined, 0);
      if (missing.length) {
        get().log('warn', `Project references ${missing.length} profile(s) not in this database: ${missing.join(', ')}. Those slots are empty.`);
      }
      get().log('info', `Imported project "${parsed.name}".`);
    } catch (err) {
      get().log('error', `Could not import project: ${(err as Error).message}`);
    }
  },

  resetAll() {
    get().pushUndo();
    set({ project: makeDefaultProject(), abxTrials: [], levelMatchResult: null });
    get().recompile(undefined, 0);
  },
}));

/**
 * Shared cable resolver handed to the compiler.
 *
 * Kept as a plain function rather than a closure over state so that the offline
 * renderer and the level-match probe resolve cables identically to live
 * playback -- an export that quietly dropped the speaker cable would be a
 * different chain from the one that was auditioned.
 */
export function resolveCable(id: string | null): CableProfile | null {
  const p = registry.get(id);
  return p && isCable(p) ? p : null;
}

function labelOf(id: string | null): string {
  if (!id) return 'none';
  const p = registry.get(id);
  return p ? `${p.manufacturer} ${p.model}` : id;
}

/**
 * Debounced recompile for continuous controls.
 *
 * Dragging a room-width slider should not rebuild an FIR sixty times a second.
 * Hardware swaps go through the immediate path instead.
 */
function scheduleRecompile(get: () => AppState & AppActions, system?: SystemSlotKey): void {
  if (recompileTimer) clearTimeout(recompileTimer);
  recompileTimer = setTimeout(() => {
    recompileTimer = null;
    get().recompile(system);
  }, 90);
}

/** Convenience selectors used across the UI. */
export const selectChain = (s: AppState, key: SystemSlotKey) => (key === 'A' ? s.project.systemA : s.project.systemB);
export const selectActiveChain = (s: AppState) => selectChain(s, s.activeSystem);
export const selectIsHeadphoneMode = (s: AppState) => {
  const out = registry.get(selectActiveChain(s).outputId);
  return !!out && isHeadphone(out);
};
export { SLOT_TO_CATEGORY };
